import axiosInstance from 'services/api/config/api';

export interface BuyPhoneNumbersRequest {
    tenantId: string;
    areaCode: number;
    count: number;
    workflowId?: string;
}

export interface BuyPhoneNumbersResponse {
    message: string;
    purchased_numbers?: string[];
    phoneNumbers?: string[];
    count: number;
}

export const buyPhoneNumbers = (data: BuyPhoneNumbersRequest): Promise<BuyPhoneNumbersResponse> => {
    return axiosInstance
        .post<BuyPhoneNumbersResponse>('/telephony/buy-phone-numbers', data)
        .then((res: { data: BuyPhoneNumbersResponse }) => res.data);
};

export interface SyncTrustProductsRequest {
    tenantId: string;
}

export interface SyncTrustProductsResponse {
    message: string;
    businessProfileSid?: string;
    cnamTrustProductSid?: string;
    shakenStirTrustProductSid?: string;
    updated: boolean;
}

export const syncTrustProducts = (data: SyncTrustProductsRequest): Promise<SyncTrustProductsResponse> => {
    return axiosInstance
        .post<SyncTrustProductsResponse>('/telephony/sync-trust-products', data)
        .then((res: { data: SyncTrustProductsResponse }) => res.data);
};

export interface SyncAllTenantsLivekitResult {
    totalTenants: number;
    processedTenants: number;
    skippedTenants: number;
    failedTenants: number;
    totalTrunks: number;
    syncedTrunks: number;
    failedTrunks: number;
    warnings?: string[];
    message: string;
}

export const syncLivekitTrunks = (): Promise<SyncAllTenantsLivekitResult> => {
    return axiosInstance
        .post<SyncAllTenantsLivekitResult>('/telephony/sync-livekit-trunks')
        .then((res: { data: SyncAllTenantsLivekitResult }) => res.data);
};

export interface DeleteTwilioAccountRequest {
    tenantId: string;
}

export interface DeleteTwilioAccountResponse {
    message: string;
    deletedNumbers: number;
    accountClosed: boolean;
}

export const deleteTwilioAccount = (data: DeleteTwilioAccountRequest): Promise<DeleteTwilioAccountResponse> => {
    return axiosInstance
        .put<DeleteTwilioAccountResponse>('/telephony/delete-account', data)
        .then((res: { data: DeleteTwilioAccountResponse }) => res.data);
};

export interface ReassignTrunkNumbersRequest {
    tenantId: string;
    oldTrunkSid: string;
}

export interface ReassignTrunkNumbersResponse {
    message: string;
}

export const reassignTrunkNumbers = (data: ReassignTrunkNumbersRequest): Promise<ReassignTrunkNumbersResponse> => {
    return axiosInstance
        .put<ReassignTrunkNumbersResponse>('/telephony/reassign-trunk-numbers', data)
        .then((res: { data: ReassignTrunkNumbersResponse }) => res.data);
};

export interface CreateWorkflowTrunkRequest {
    tenantId: string;
    workflowId: string;
}

export interface CreateWorkflowTrunkResponse {
    twilioTrunkSid: string;
    livekitTrunkSid: string;
    terminationURI: string;
    message: string;
}

export const createWorkflowTrunk = (data: CreateWorkflowTrunkRequest): Promise<CreateWorkflowTrunkResponse> => {
    return axiosInstance
        .post<CreateWorkflowTrunkResponse>('/telephony/create-workflow-trunk', data)
        .then((res: { data: CreateWorkflowTrunkResponse }) => res.data);
};

export interface TestSpamCarrierRequest {
    tenantId?: string;
}

export const testSpamCarrier = (data: TestSpamCarrierRequest): void => {
    const params = data.tenantId ? { tenant_id: data.tenantId } : {};
    // Fire and forget: the run is dispatched in the background and answers 202
    // straight away, so there is no result worth waiting for here.
    axiosInstance.post('/carrier-tests/runs', {}, { params }).catch((error: unknown) => {
        console.error('Error initiating spam carrier test:', error);
    });
};

export type OrphanNumberProvider = 'twilio' | 'telnyx';

export interface OrphanNumber {
    phoneNumber: string;
    providerSid: string;
    providerTrunkId: string;
}

export interface OrphanNumbersResponse {
    provider: OrphanNumberProvider;
    providerNumberCount: number;
    count: number;
    orphans: OrphanNumber[];
}

export const getOrphanNumbers = (tenantId: string): Promise<OrphanNumbersResponse> => {
    return axiosInstance
        .get<OrphanNumbersResponse>('/telephony/orphan-numbers', {
            params: { tenantId },
        })
        .then((res: { data: OrphanNumbersResponse }) => res.data);
};

export interface ReleaseOrphanNumbersRequest {
    tenantId: string;
    phoneNumbers: string[];
}

export interface ReleaseOrphanNumbersResponse {
    releasedNumbers: string[];
    failedToReleaseNumbers: string[];
    skippedNumbers: string[];
    livekitOutboundSynced: boolean;
    livekitInboundSynced: boolean;
    deletedInboundTrunks: string[];
    warnings?: string;
}

export const releaseOrphanNumbers = (data: ReleaseOrphanNumbersRequest): Promise<ReleaseOrphanNumbersResponse> => {
    return axiosInstance
        .post<ReleaseOrphanNumbersResponse>('/telephony/orphan-numbers/release', data)
        .then((res: { data: ReleaseOrphanNumbersResponse }) => res.data);
};

const phoneNumberService = {
    buyPhoneNumbers,
    syncTrustProducts,
    syncLivekitTrunks,
    deleteTwilioAccount,
    reassignTrunkNumbers,
    createWorkflowTrunk,
    testSpamCarrier,
    getOrphanNumbers,
    releaseOrphanNumbers,
};

export default phoneNumberService;
